<template>
  <div class="profile-goals">
    <ProfilePageHeader title="Цели" />

    <div class="form-card">
      <button
        v-for="g in currentGoals"
        :key="g.id"
        class="goal-card"
        :class="{ selected: selected.includes(g.id) }"
        @click="toggleGoal(g.id)"
      >
        <span class="goal-emoji">{{ g.emoji }}</span>
        <div class="goal-text">
          <div class="goal-title">{{ g.title }}</div>
          <div v-if="g.desc" class="goal-desc">{{ g.desc }}</div>
        </div>
        <!-- Radio for sport, checkbox for others -->
        <div v-if="isSingleSelect" class="goal-radio" :class="{ checked: selected.includes(g.id) }" />
        <div v-else class="goal-check" :class="{ checked: selected.includes(g.id) }">
          <svg v-if="selected.includes(g.id)" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 7l3 3 5-6" stroke="#FAF3E8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </button>

      <!-- Своя цель -->
      <div class="own-goal-block">
        <label class="field-label">СВОЯ ЦЕЛЬ</label>
        <input
          v-model="ownGoal"
          class="form-input"
          placeholder="напишите о своём"
        />
      </div>
    </div>

    <!-- Автосохранение статус -->
    <p v-if="saveStatus" class="save-status">{{ saveStatus }}</p>

    <!-- Назад в Профиль -->
    <button class="btn-primary btn-back" @click="$router.push('/profile')">
      Назад в Профиль
    </button>
  </div>
</template>

<script setup>
import ProfilePageHeader from '../components/ProfilePageHeader.vue'
import { ref, computed, watch, onMounted } from 'vue'
import { useAuthStore } from '../store/auth.js'

const authStore = useAuthStore()

onMounted(() => { window.scrollTo(0, 0) })

// ── Segment from user_type ──
const USER_TYPE_TO_SEGMENT = { rpp: 'masha', control: 'control', training: 'sport' }
const segment = computed(() => USER_TYPE_TO_SEGMENT[authStore.user?.user_type] || 'control')

const isSingleSelect = computed(() => segment.value === 'sport')

// ── Goal lists per segment (same as StepGoals.vue) ──
const mashaGoals = [
  { id: 'plate', emoji: '⚖️', title: 'Приблизить питание к пропорциям Метода Тарелки', desc: '' },
  { id: 'trig', emoji: '🌊', title: 'Замечать, что запускает переедание', desc: '' },
  { id: 'body', emoji: '🥄', title: 'Научиться слышать сигналы голода и сытости', desc: '' },
  { id: 'swing', emoji: '💝', title: 'Выйти из качелей «ограничение — срыв»', desc: '' },
  { id: 'care', emoji: '💚', title: 'Относиться к себе с заботой, а не с претензией', desc: '' },
]

const controlGoals = [
  { id: 'full', emoji: '🧭', title: 'Контроль питания по цифрам', desc: 'Все цифры на виду: КБЖУ, клетчатка, витамины и минералы, тренды по дням и неделям' },
  { id: 'cut', emoji: '📉', title: 'Снижение веса', desc: 'Контролируемый дефицит (5%-20%), задается в профиле. Дефицит считается от нормы калорий.' },
  { id: 'bulk', emoji: '📈', title: 'Набор мышечной массы', desc: 'Контролируемый профицит (200-400 ккал) и повышенный белок (1.6 - 2.0 г/кг), задается в профиле.' },
  { id: 'recomp', emoji: '🔄', title: 'Рекомпозиция', desc: 'Удержание веса, при смене состава тела: меньше жира, больше мышц. Небольшой дефицит, повышенный белок, силовые в основе.' },
  { id: 'maintain', emoji: '⚖️', title: 'Поддержание веса, питание по Методу Тарелки', desc: 'Калории на уровне нормы. Фокус смещен на БЖУ, клетчатку и регулярность.' },
]

const sportGoals = [
  { id: 'bulk', emoji: '📈', title: 'Набор массы', desc: 'Профицит 200–400 ккал. Белок 1.6–2.2 г/кг. Цель — рост мышц при минимуме лишнего жира.' },
  { id: 'cut', emoji: '🔻', title: 'Снижение процента жира', desc: 'Дефицит ~300–500 ккал. Белок высокий (до 2.2 г/кг), чтобы терять больше жира, а не мышц.' },
  { id: 'maintain', emoji: '⚖️', title: 'Поддержание формы', desc: 'Калории на уровне расхода. Фокус на стабильности макросов и регулярности.' },
  { id: 'recomp', emoji: '🔄', title: 'Рекомпозиция', desc: 'Вес держится, состав тела меняется: меньше жира, больше мышц. Небольшой дефицит или норма, высокий белок, силовые в основе.' },
  { id: 'endurance', emoji: '🏃', title: 'Выносливость и восстановление', desc: 'Цель не масса, а результат на дистанции. Акцент на углеводах под объём тренировок и на восстановлении.' },
]

const currentGoals = computed(() => {
  if (segment.value === 'sport') return sportGoals
  if (segment.value === 'masha') return mashaGoals
  return controlGoals
})

// ── Pre-fill from existing data ──
const prev = authStore.user?.goals_data || {}
const validIds = computed(() => currentGoals.value.map(g => g.id))

// Only pre-fill IDs that exist in the current segment's goal list
const initialSelected = (prev.selected || []).filter(id => validIds.value.includes(id))
const selected = ref([...initialSelected])
const ownGoal = ref(prev.own_goal || '')

// ── Conflicting pairs (control only) ──
const conflictingPairs = [['cut', 'bulk']]

function getConflict(id) {
  if (segment.value !== 'control') return null
  for (const [a, b] of conflictingPairs) {
    if (id === a && selected.value.includes(b)) return b
    if (id === b && selected.value.includes(a)) return a
  }
  return null
}

// ── Selection logic ──
function toggleGoal(id) {
  if (isSingleSelect.value) {
    // Sport: radio — only one at a time
    selected.value = selected.value.includes(id) ? [] : [id]
    return
  }

  // Multi-select (masha/control)
  if (selected.value.includes(id)) {
    selected.value = selected.value.filter(x => x !== id)
  } else {
    // Block conflicting selection for control
    if (getConflict(id)) return
    selected.value = [...selected.value, id]
  }
}

// ── Auto-save with debounce ──
const saveStatus = ref('')
let saveTimer = null

function buildData() {
  return {
    selected: [...selected.value],
    own_goal: ownGoal.value.trim() || null,
  }
}

function triggerAutoSave() {
  saveStatus.value = ''
  clearTimeout(saveTimer)
  saveTimer = setTimeout(async () => {
    try {
      saveStatus.value = 'Сохранение...'
      await authStore.updateProfile({ goals_data: buildData() })
      saveStatus.value = 'Сохранено'
      setTimeout(() => { if (saveStatus.value === 'Сохранено') saveStatus.value = '' }, 2000)
    } catch {
      saveStatus.value = 'Ошибка сохранения'
    }
  }, 1000)
}

watch(selected, triggerAutoSave, { deep: true })
watch(ownGoal, triggerAutoSave)
</script>

<style scoped>
.profile-goals {
  min-height: 100vh;
  min-height: 100dvh;
  background: #FAF3E8;
  padding: 48px 16px 100px;
  padding-top: max(48px, calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 16px));
}



/* ── Form card ── */
.form-card {
  background: #FFFCF3;
  border-radius: 20px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 1px 4px rgba(65,41,26,0.06);
  margin-bottom: 16px;
}

.field-label {
  font-size: var(--fs-11);
  letter-spacing: 1.5px;
  font-weight: 700;
  color: var(--warm);
  text-transform: uppercase;
}

/* ── Goal cards ── */
.goal-card {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
  border: 2px solid rgba(65,41,26,0.06);
  border-radius: 16px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: border-color 0.2s;
}
.goal-card.selected {
  border-color: var(--ink);
}
.goal-emoji {
  font-size: var(--fs-20);
  line-height: 1;
  margin-top: 1px;
  width: 24px;
  text-align: center;
  flex-shrink: 0;
}
.goal-text {
  flex: 1;
  min-width: 0;
}
.goal-title {
  font-weight: 400;
  font-size: var(--fs-15);
  line-height: 1.25;
  color: var(--ink);
}
.goal-desc {
  font-size: var(--fs-13);
  color: var(--ink-70);
  margin-top: 4px;
  line-height: 1.4;
}

/* ── Checkbox (multi-select) ── */
.goal-check {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid rgba(65,41,26,0.2);
  flex-shrink: 0;
  margin-top: 2px;
  transition: background 0.2s, border-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.goal-check.checked {
  background: #41291A;
  border-color: var(--ink);
}

/* ── Radio (single-select / sport) ── */
.goal-radio {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid rgba(65,41,26,0.2);
  flex-shrink: 0;
  margin-top: 2px;
  transition: border-color 0.2s;
  position: relative;
}
.goal-radio.checked {
  border-color: var(--ink);
}
.goal-radio.checked::after {
  content: '';
  position: absolute;
  top: 4px;
  left: 4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #41291A;
}

/* ── Own goal ── */
.own-goal-block {
  padding-top: 4px;
}

.form-input {
  width: 100%;
  padding: 12px 14px;
  border: 2px solid rgba(65,41,26,0.15);
  border-radius: 12px;
  background: transparent;
  font-size: var(--fs-16);
  color: var(--ink);
  font-family: inherit;
  box-sizing: border-box;
  margin-top: 6px;
}
.form-input:focus {
  outline: none;
  border-color: var(--ink);
}

/* ── Save status ── */
.save-status {
  font-size: var(--fs-12);
  color: var(--ink-70);
  text-align: center;
  margin-bottom: 12px;
}

/* ── Buttons ── */

.btn-back {
  margin-top: 4px;
}
</style>
